import React, { useState, useEffect } from 'react';

const PartnerModal = ({ show, onClose, onSave, loading, initialData }) => {
  const [formData, setFormData] = useState({
    nom: '',
    presentation: '',
    site_url: '',
    categorie: 'Général',
    ordre: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  // Synchronisation du formulaire selon si on crée ou on modifie
  useEffect(() => {
    if (show) {
      if (initialData) {
        // MODE ÉDITION : On remplit avec les infos existantes
        setFormData({
          nom: initialData.nom || '',
          presentation: initialData.presentation || '',
          site_url: initialData.site_url || '',
          categorie: initialData.categorie || 'Général',
          ordre: initialData.ordre || 0
        });
        // Si un logo existe déjà, on prépare l'URL pour l'aperçu
        if (initialData.logo_url) {
          const fullUrl = initialData.logo_url.startsWith('http') 
            ? initialData.logo_url 
            : `http://localhost:5000${initialData.logo_url}`;
          setPreview(fullUrl);
        } else {
          setPreview(null);
        }
      } else {
        // MODE CRÉATION : On vide tout
        setFormData({ nom: '', presentation: '', site_url: '', categorie: 'Général', ordre: 0 });
        setPreview(null);
      }
      setSelectedFile(null);
    }
  }, [show, initialData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file)); // Aperçu du nouveau fichier
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = new FormData();
    data.append('nom', formData.nom);
    data.append('presentation', formData.presentation);
    data.append('site_url', formData.site_url);
    data.append('categorie', formData.categorie);
    data.append('ordre', formData.ordre);
    
    if (selectedFile) {
      data.append('logo', selectedFile);
    }

    onSave(data);
  };

  if (!show) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content bg-dark text-white border-secondary rounded-4 shadow-lg">
          
          <div className="modal-header border-secondary p-4">
            <h5 className="modal-title fw-black text-uppercase tracking-tighter">
              <i className={`bi ${initialData ? 'bi-pencil-square' : 'bi-hand-thumbs-up-fill'} text-primary me-2`}></i>
              {initialData ? 'Modifier le Partenaire' : 'Nouveau Partenaire'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
              <div className="row g-3">
                
                {/* NOM */}
                <div className="col-12">
                  <label className="form-label small fw-bold text-secondary text-uppercase">Nom du partenaire</label>
                  <input 
                    type="text" 
                    className="form-control bg-black border-secondary text-white shadow-none" 
                    required 
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    placeholder="Ex: Ti Chef 974"
                  />
                </div>

                {/* LOGO UPLOAD */}
                <div className="col-12">
                  <label className="form-label small fw-bold text-secondary text-uppercase">
                    {initialData ? 'Changer le Logo (Optionnel)' : 'Logo (Image)'}
                  </label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="bg-black border border-secondary rounded-3 d-flex align-items-center justify-content-center overflow-hidden" style={{ width: '80px', height: '80px', flexShrink: 0 }}>
                      {preview ? (
                        <img src={preview} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <i className="bi bi-image text-secondary fs-3"></i>
                      )}
                    </div>
                    <input 
                      type="file" 
                      className="form-control form-control-sm bg-black border-secondary text-white shadow-none" 
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>

                {/* PRÉSENTATION */}
                <div className="col-12">
                  <label className="form-label small fw-bold text-secondary text-uppercase">Petite présentation</label>
                  <textarea 
                    className="form-control bg-black border-secondary text-white shadow-none" 
                    rows="3" 
                    required
                    value={formData.presentation}
                    onChange={(e) => setFormData({...formData, presentation: e.target.value})}
                    placeholder="Décrivez brièvement l'activité..."
                  ></textarea>
                </div>

                {/* LIEN SITE WEB */}
                <div className="col-md-8">
                  <label className="form-label small fw-bold text-secondary text-uppercase">Lien du site (URL)</label>
                  <input 
                    type="url" 
                    className="form-control bg-black border-secondary text-white shadow-none" 
                    value={formData.site_url}
                    onChange={(e) => setFormData({...formData, site_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                {/* CATÉGORIE */}
                <div className="col-md-4">
                  <label className="form-label small fw-bold text-secondary text-uppercase">Catégorie</label>
                  <select 
                    className="form-select bg-black border-secondary text-white shadow-none"
                    value={formData.categorie}
                    onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                  >
                    <option value="Général">Général</option>
                    <option value="Gastronomie">Gastronomie</option>
                    <option value="Bien-être">Bien-être</option>
                    <option value="Automobile">Automobile</option>
                    <option value="Services">Services</option>
                  </select>
                </div>

              </div>
            </div>

            <div className="modal-footer border-secondary p-4 pt-0">
              <button type="button" className="btn btn-outline-secondary rounded-pill px-4 fw-bold" onClick={onClose} disabled={loading}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary rounded-pill px-4 fw-black text-uppercase shadow-sm" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-check-lg me-2"></i>}
                {initialData ? 'Mettre à jour' : 'Enregistrer le partenaire'}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default PartnerModal;